/**
 * @description Component-ScaleOrderCount
 */
import { useTranslation } from 'react-i18next';
import './index.less';

import Alert from '@/components/Alert';
import { DEFAULT_ORDER_COUNT, DEFAULT_STEP_RATIO, MIN_ORDER_COUNT } from '@/constants/trade';
import { WrappedPair } from '@/entities/WrappedPair';
import { useCurrentPairFromUrl } from '@/features/pair/hook';
import { useUpdatePriceByTicks } from '@/features/trade/hooks';
import { useTradeStore } from '@/features/trade/store';
import { useChainId } from '@/hooks/web3/useChain';
import StepRatioSelector from '@/pages/trade/CoinPriceChart/StepRatioSelector';
import { Context } from '@derivation-tech/context';
import { Side } from '@synfutures/sdks-perp';
import classNames from 'classnames';
import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
interface IPropTypes {
  children?: React.ReactNode;
  className?: string;
  lowerLimitTick: number;
  upperLimitTick: number;
  tradeSide: Side;
  sdkContext: Context | undefined;
  pair: WrappedPair | undefined;
}
const ScaleOrderCount: FC<IPropTypes> = function ({ tradeSide, pair, lowerLimitTick, upperLimitTick }) {
  const chainId = useChainId();
  const { t } = useTranslation();
  const [count, setCount] = useState<number>(DEFAULT_ORDER_COUNT);
  const {
    orderCount: storeOrderCount,
    scaleLimitStepRatio: storeStepRatio,
    setOrderCount,
    lowerTick,
    upperTick,
    setScaleLimitStepRatio: setStoreStepRatio,
    setPairScaleLimitStepRatio,
  } = useTradeStore();
  const items = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  const gap = useMemo(() => upperLimitTick - lowerLimitTick, [lowerLimitTick, upperLimitTick]);
  const [gapWidth, setGapWidth] = useState(4);
  const bgWidth = useMemo(() => count * 20 + (count - 1) * gapWidth + 4, [count, gapWidth]);

  const updatePricesByTicks = useUpdatePriceByTicks();
  const ordersRef = useRef<HTMLDivElement>(null);
  const currentPair = useCurrentPairFromUrl(chainId);
  const [stepRatio, setStepRatio] = useState(DEFAULT_STEP_RATIO);

  const pairKey = useMemo(() => currentPair?.id, [currentPair?.id]);

  useEffect(() => {
    if (pairKey) {
      const saved = useTradeStore.getState().pairScaleLimitStepRatioMap[pairKey];
      setStepRatio(saved ?? DEFAULT_STEP_RATIO);
    } else {
      setStepRatio(DEFAULT_STEP_RATIO);
    }
  }, [pairKey]);
  useEffect(() => {
    const getGapWidth = () => {
      if (ordersRef.current) {
        const ordersWidth = ordersRef.current.offsetWidth;
        if (ordersWidth > 16 + 180) {
          const gapWidth = (ordersWidth - 16 - 180) / 8;
          setGapWidth(gapWidth);
        }
      }
    };
    getGapWidth();
    window.addEventListener('resize', getGapWidth);
    return () => {
      window.removeEventListener('resize', getGapWidth);
    };
  }, [setGapWidth]);

  const onOrderSelect = useCallback(
    (orderCount?: number, stepRatio?: number) => {
      if (!chainId || !lowerTick) return;
      !!orderCount && setCount(orderCount);
      orderCount = orderCount || storeOrderCount;
      stepRatio = stepRatio || storeStepRatio;
      const countTicksGap = (orderCount - 1) * stepRatio;
      let newLowerTick = lowerTick;
      let newUpperTick = upperTick;
      if (tradeSide === Side.LONG) {
        newLowerTick = newUpperTick - countTicksGap;
        if (newLowerTick < lowerLimitTick) {
          newLowerTick = lowerLimitTick;
          newUpperTick = newLowerTick + countTicksGap;
        }
      }
      if (tradeSide === Side.SHORT) {
        newUpperTick = newLowerTick + countTicksGap;
        if (newUpperTick > upperLimitTick) {
          newUpperTick = upperLimitTick;
          newLowerTick = newUpperTick - countTicksGap;
        }
      }
      pair && updatePricesByTicks(newLowerTick, newUpperTick, pair);
    },
    [
      chainId,
      lowerTick,
      storeOrderCount,
      storeStepRatio,
      upperTick,
      tradeSide,
      pair,
      updatePricesByTicks,
      lowerLimitTick,
      upperLimitTick,
    ],
  );
  useEffect(() => {
    setStoreStepRatio(stepRatio);
    setOrderCount(count);
  }, [stepRatio, count, setOrderCount, setStoreStepRatio]);

  return (
    <div className="syn-scale-limit-form-section horizontal">
      <div className="syn-scale-limit-form-section-column left">
        <div className="syn-scale-limit-form-section-title">{t('common.tradePage.orderCount')}</div>
        <div className="syn-scale-limit-form-section-order-counts" ref={ordersRef}>
          <div
            className={classNames('syn-scale-limit-form-section-order-counts-selected-bg', Side[tradeSide])}
            style={{ width: bgWidth }}
          />
          {items.map((orderCount) => (
            <div
              onClick={() => {
                if (orderCount === 1 || (!isNaN(gap) && orderCount * stepRatio > gap)) return;
                onOrderSelect(orderCount, stepRatio);
              }}
              key={orderCount}
              className={classNames(
                'syn-scale-limit-form-section-order-counts-item',
                {
                  selected: count >= orderCount,
                  disabled: orderCount === 1 || (!isNaN(gap) && orderCount * stepRatio > gap),
                },
                Side[tradeSide],
              )}>
              {orderCount}
            </div>
          ))}
        </div>
      </div>
      <div className="syn-scale-limit-form-section-column right">
        <div className="syn-scale-limit-form-section-title">{t('common.tradePage.gap')}</div>
        <StepRatioSelector
          onChange={(stepRatio) => {
            setStepRatio(stepRatio);
            pairKey && setPairScaleLimitStepRatio(pairKey, stepRatio);
            let countFinal = count;
            let total = countFinal * stepRatio;
            if (total > gap) {
              let index = items.length - 1;
              while (total > gap) {
                index = index - 1;
                countFinal = items[index];
                total = countFinal * stepRatio;
              }
            }
            onOrderSelect(countFinal, stepRatio);
          }}
          popupMatchSelectWidth={true}
          stepRatio={stepRatio}
          dropdownStyle={{ width: 73 }}
          withBaseBPs={true}
          dropdownAlign={{ points: ['tr', 'br'], offset: [0, 12] }}
          imr={pair?.rootInstrument.metaFutures.setting.initialMarginRatio}
        />
      </div>
      {count < MIN_ORDER_COUNT && (
        <Alert type="warning" message={t('common.tradePage.warningMsg.priceRangeLow')} showIcon={true} />
      )}
    </div>
  );
};

export default memo(ScaleOrderCount);
